export enum DriverName {
  HamiltonStarlet = 'hamilton_starlet',
  Biomek = 'beckman_biomek',
  HettichSbs300 = 'hettich_sbs_300',
}

export enum InstrumentCapability {
  SlotAllocation = 'slotAllocation',
  OutputSlotRequired = 'outputSlotRequired',
  OutputSlotDeclaredByTask = 'outputSlotDeclaredByTask',
  /**
   * Controls whether slot strings can use range syntax (e.g., "1-5, 10, 12").
   * Instruments WITH this capability accept range notation: "1-5" expands to slots 1,2,3,4,5.
   * Instruments WITHOUT this capability only accept comma-separated format: "1, 5, 10".
   *
   * Note: This is separate from OutputSlotRequired, which controls whether both
   * input AND output slots must be defined.
   */
  SlotRangeSupport = 'slotRangeSupport',
}

const INSTRUMENT_CAPABILITIES = new Map<DriverName, InstrumentCapability[]>([
  [
    DriverName.HamiltonStarlet,
    [
      InstrumentCapability.SlotAllocation,
      InstrumentCapability.OutputSlotRequired,
      InstrumentCapability.SlotRangeSupport,
    ],
  ],
  [
    DriverName.Biomek,
    [
      InstrumentCapability.SlotAllocation,
      InstrumentCapability.OutputSlotRequired,
      InstrumentCapability.SlotRangeSupport,
    ],
  ],
  [DriverName.HettichSbs300, [InstrumentCapability.SlotAllocation]],
]);

export const TASK_TYPE_TO_SLOT_ALLOCATION_BY_DRIVER = new Map<
  DriverName,
  Set<string>
>([
  [DriverName.HamiltonStarlet, new Set(['run_liquid_handler', 'run'])],
  [DriverName.Biomek, new Set(['run_liquid_handler', 'run'])],
  [DriverName.HettichSbs300, new Set(['spin'])],
]);

export const hasCapability = (
  driverName: string,
  capability: InstrumentCapability
): boolean =>
  INSTRUMENT_CAPABILITIES.get(driverName as DriverName)?.includes(capability) ??
  false;

export const isSlotAllocatingInstrument = (driverName: string): boolean =>
  hasCapability(driverName, InstrumentCapability.SlotAllocation);

export const needsOutputSlot = (driverName: string): boolean =>
  hasCapability(driverName, InstrumentCapability.OutputSlotRequired);

/**
 * Returns true if instrument *in general* supports slot allocation,
 * but this specific task type does not support Labware & Deck slot input.
 */
export const isTaskDoesNotSupportSlotAllocating = (
  driverName: string,
  taskType: string
): boolean => {
  // Continuous loading tasks don't support slot allocation
  if (taskType === 'continuousLoading') {
    return true;
  }

  const taskTypes = TASK_TYPE_TO_SLOT_ALLOCATION_BY_DRIVER.get(
    driverName as DriverName
  );

  return (
    hasCapability(driverName, InstrumentCapability.SlotAllocation) &&
    (!taskTypes || !taskTypes.has(taskType))
  );
};

/**
 * Returns true if the instrument supports range syntax in slot strings (e.g., "1-5, 10, 12").
 *
 * Instruments with SlotRangeSupport (Hamilton, Biomek):
 * - Accept range notation: "1-5" expands to slots 1,2,3,4,5
 * - Accept comma-separated: "1, 5, 10"
 * - Accept mixed: "1-5, 10, 12"
 *
 * Instruments without SlotRangeSupport (Hettich):
 * - Only accept comma-separated: "1, 5, 10"
 *
 * Note: This controls slot string FORMAT, not whether both input/output slots are required.
 * See OutputSlotRequired capability for that behavior.
 */
export const supportsSlotRanges = (driverName?: string): boolean =>
  hasCapability(driverName || '', InstrumentCapability.SlotRangeSupport);
